/**
 * hangulToQwerty — 두벌식 자판 기준으로 한글을 영문 키로 변환한다.
 *
 * 아이디 입력란처럼 영문만 받아야 하지만, IME 가 켜진 채 타이핑하면
 * 한글이 들어온다. 이때 글자를 지우는 대신 같은 키 위치의 영문으로 바꿔준다.
 *   예) "안녕" → "dkssud", "ㅁ" → "a", "까" → "Rk"
 *
 * 합성 음절(가~힣)은 초/중/종성으로 분해 후 각 자모를 키로 매핑하고,
 * 단독 호환 자모(ㄱ~ㅣ)도 직접 매핑한다. 한글이 아닌 문자는 그대로 둔다.
 */

// 초성 19자 (ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ)
const CHO = [
  "r", "R", "s", "e", "E", "f", "a", "q", "Q", "t",
  "T", "d", "w", "W", "c", "z", "x", "v", "g",
];

// 중성 21자 (ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ)
const JUNG = [
  "k", "o", "i", "O", "j", "p", "u", "P", "h", "hk",
  "ho", "hl", "y", "n", "nj", "np", "nl", "b", "m", "ml", "l",
];

// 종성 28자 (없음 포함)
const JONG = [
  "", "r", "R", "rt", "s", "sw", "sg", "e", "f", "fr",
  "fa", "fq", "ft", "fx", "fv", "fg", "a", "q", "qt", "t",
  "T", "d", "w", "c", "z", "x", "v", "g",
];

// 단독 호환 자모 (U+3131~U+3163)
const COMPAT: Record<string, string> = {
  "ㄱ": "r", "ㄲ": "R", "ㄳ": "rt", "ㄴ": "s", "ㄵ": "sw", "ㄶ": "sg",
  "ㄷ": "e", "ㄸ": "E", "ㄹ": "f", "ㄺ": "fr", "ㄻ": "fa", "ㄼ": "fq",
  "ㄽ": "ft", "ㄾ": "fx", "ㄿ": "fv", "ㅀ": "fg", "ㅁ": "a", "ㅂ": "q",
  "ㅃ": "Q", "ㅄ": "qt", "ㅅ": "t", "ㅆ": "T", "ㅇ": "d", "ㅈ": "w",
  "ㅉ": "W", "ㅊ": "c", "ㅋ": "z", "ㅌ": "x", "ㅍ": "v", "ㅎ": "g",
  "ㅏ": "k", "ㅐ": "o", "ㅑ": "i", "ㅒ": "O", "ㅓ": "j", "ㅔ": "p",
  "ㅕ": "u", "ㅖ": "P", "ㅗ": "h", "ㅘ": "hk", "ㅙ": "ho", "ㅚ": "hl",
  "ㅛ": "y", "ㅜ": "n", "ㅝ": "nj", "ㅞ": "np", "ㅟ": "nl", "ㅠ": "b",
  "ㅡ": "m", "ㅢ": "ml", "ㅣ": "l",
};

const SYL_BASE = 0xac00;
const SYL_LAST = 0xd7a3;

export function hangulToQwerty(input: string): string {
  let out = "";
  for (const ch of input) {
    const code = ch.codePointAt(0)!;
    if (code >= SYL_BASE && code <= SYL_LAST) {
      const offset = code - SYL_BASE;
      const jong = offset % 28;
      const jung = Math.floor(offset / 28) % 21;
      const cho = Math.floor(offset / (28 * 21));
      out += CHO[cho] + JUNG[jung] + JONG[jong];
    } else if (ch in COMPAT) {
      out += COMPAT[ch];
    } else {
      out += ch;
    }
  }
  return out;
}
